import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type SupportedLocale = 'en' | 'el' | 'de' | 'fr' | 'es' | 'it';

export interface LocaleOption {
  code: SupportedLocale;
  label: string;
  currency: string;
  dateFormat: Intl.DateTimeFormatOptions;
}

export const SUPPORTED_LOCALES: LocaleOption[] = [
  { code: 'en', label: 'English (US)', currency: 'USD', dateFormat: { year: 'numeric', month: 'short', day: 'numeric' } },
  { code: 'el', label: 'Ελληνικά (Greek)', currency: 'EUR', dateFormat: { year: 'numeric', month: 'short', day: 'numeric' } },
  { code: 'de', label: 'Deutsch (German)', currency: 'EUR', dateFormat: { year: 'numeric', month: 'short', day: 'numeric' } },
  { code: 'fr', label: 'Français (French)', currency: 'EUR', dateFormat: { year: 'numeric', month: 'short', day: 'numeric' } },
  { code: 'es', label: 'Español (Spanish)', currency: 'EUR', dateFormat: { year: 'numeric', month: 'short', day: 'numeric' } },
  { code: 'it', label: 'Italiano (Italian)', currency: 'EUR', dateFormat: { year: 'numeric', month: 'short', day: 'numeric' } },
];

interface LocaleContextType {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
  getLocaleOption: () => LocaleOption;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const getLocaleFromBrowser = (): SupportedLocale => {
  const browserLang = navigator.language?.split('-')[0] as SupportedLocale;
  return SUPPORTED_LOCALES.some(l => l.code === browserLang) ? browserLang : 'en';
};

export const LocaleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [locale, setLocaleState] = useState<SupportedLocale>(() => {
    // Priority: 1. User preference from profile, 2. localStorage, 3. Browser language
    const stored = localStorage.getItem('userLocale') as SupportedLocale | null;
    if (stored && SUPPORTED_LOCALES.some(l => l.code === stored)) {
      return stored;
    }
    return getLocaleFromBrowser();
  });

  // Sync locale with user's language preference from their profile
  useEffect(() => {
    if (user?.personalInfo?.language) {
      const userLocale = user.personalInfo.language as SupportedLocale;
      if (SUPPORTED_LOCALES.some(l => l.code === userLocale)) {
        setLocaleState(userLocale);
        localStorage.setItem('userLocale', userLocale);
      }
    }
  }, [user?.personalInfo?.language]);

  const setLocale = (newLocale: SupportedLocale) => {
    setLocaleState(newLocale);
    localStorage.setItem('userLocale', newLocale);
  };

  const getLocaleOption = (): LocaleOption => {
    return SUPPORTED_LOCALES.find(l => l.code === locale) || SUPPORTED_LOCALES[0];
  };

  const formatDate = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return 'Invalid Date';
      
      const defaultOptions = getLocaleOption().dateFormat;
      return new Intl.DateTimeFormat(locale, options || defaultOptions).format(dateObj);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount: number, currency?: string): string => {
    try {
      const localeOption = getLocaleOption();
      const currencyCode = currency || localeOption.currency;
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      console.error('Error formatting currency:', error);
      return `${amount.toFixed(2)}`;
    }
  };

  const formatNumber = (num: number, options?: Intl.NumberFormatOptions): string => {
    try {
      return new Intl.NumberFormat(locale, options).format(num);
    } catch (error) {
      console.error('Error formatting number:', error);
      return String(num);
    }
  };

  return (
    <LocaleContext.Provider value={{ 
      locale, 
      setLocale, 
      formatDate, 
      formatCurrency,
      formatNumber,
      getLocaleOption 
    }}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

export default LocaleContext;
